import { AGWASuiteApiClientBase, ApiResponse } from '../agwasuite-api';
import {
  AssignUserToSiteInput,
  UpdateUserSiteAssignmentInput,
  UserSiteAssignmentResult,
  UserSiteAssignmentsResult,
} from '../agwa-api-model';

/**
 * API client for managing user site assignments.
 */
export class SiteAssignmentsApi extends AGWASuiteApiClientBase {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  /**
   * Gets all site assignments for a specific user.
   */
  async getUserAssignments(
    userId: string,
    includeInactive = false
  ): Promise<ApiResponse<UserSiteAssignmentsResult>> {
    const params = includeInactive ? '?includeInactive=true' : '';
    const path = `/api/UserSiteAssignments/user/${encodeURIComponent(userId)}${params}`;
    return this.get<UserSiteAssignmentsResult>(path);
  }

  /**
   * Gets all users assigned to a specific site.
   */
  async getSiteUsers(
    siteId: string,
    includeInactive = false
  ): Promise<ApiResponse<UserSiteAssignmentsResult>> {
    const params = includeInactive ? '?includeInactive=true' : '';
    const path = `/api/UserSiteAssignments/site/${encodeURIComponent(siteId)}${params}`;
    return this.get<UserSiteAssignmentsResult>(path);
  }

  /**
   * Assigns a user to a site.
   */
  async assignUserToSite(
    input: AssignUserToSiteInput
  ): Promise<ApiResponse<UserSiteAssignmentResult>> {
    return this.post<AssignUserToSiteInput, UserSiteAssignmentResult>(
      '/api/UserSiteAssignments',
      input
    );
  }

  /**
   * Updates an existing site assignment.
   */
  async updateAssignment(
    assignmentId: string,
    input: Omit<UpdateUserSiteAssignmentInput, 'assignmentId'>
  ): Promise<ApiResponse<UserSiteAssignmentResult>> {
    const path = `/api/UserSiteAssignments/${encodeURIComponent(assignmentId)}`;
    return this.put<UpdateUserSiteAssignmentInput, UserSiteAssignmentResult>(
      path,
      { ...input, assignmentId }
    );
  }

  /**
   * Removes a user from a site (deactivates the assignment).
   */
  async removeAssignment(
    assignmentId: string
  ): Promise<ApiResponse<UserSiteAssignmentResult>> {
    const path = `/api/UserSiteAssignments/${encodeURIComponent(assignmentId)}`;
    return this.delete<UserSiteAssignmentResult>(path);
  }
}
